import React, { useState, useCallback, useRef, lazy, Suspense } from 'react';
import html2canvas from 'html2canvas';
import { Download, RotateCcw, Sparkles, Grid, Type, Palette, Image as ImageIcon, Sliders, SunMedium, Sticker } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import CollageGrid from './CollageGrid';
import TemplateSelector, { TEMPLATES, Template } from './TemplateSelector';
import SettingsPanel from './SettingsPanel';
import FiltersPanel, { FILTERS } from './FiltersPanel';
import TextOverlay, { TextItem } from './TextOverlay';
import StickerOverlay, { StickerItem } from './StickerOverlay';
import SizeSelector from './SizeSelector';
import BackgroundPanel from './BackgroundPanel';
import TemplateGenerator from './TemplateGenerator';
import AdjustmentsPanel, { ImageAdjustments, defaultAdjustments } from './AdjustmentsPanel';
import TextSizeSelector, { TextSize } from './TextSizeSelector';
import { ImageTransform } from './ImageEditor';
import { useLanguage } from '@/contexts/LanguageContext';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import ThemeToggle from '@/components/ThemeToggle';
import SEOHead from '@/components/SEOHead';

// Lazy load heavy sections that are below the fold
const FeaturesSection = lazy(() => import('@/components/FeaturesSection'));
const SocialShare = lazy(() => import('@/components/SocialShare'));
const FAQSection = lazy(() => import('@/components/FAQSection'));
const SEOIntroSection = lazy(() => import('@/components/SEOIntroSection'));
const Footer = lazy(() => import('@/components/Footer'));

interface CellData {
  id: string;
  image: string | null;  // URL.createObjectURL for preview
  originalFile?: File;    // Original file for high-quality export
  span?: { col?: number; row?: number };
  transform?: ImageTransform;
}

interface HistoryState {
  past: ImageTransform[];
  future: ImageTransform[];
}

const defaultTransform: ImageTransform = {
  flipH: false,
  flipV: false,
  offsetX: 0, // Смещение от центра в процентах от размера ячейки
  offsetY: 0,
  scale: 1,
};

const CollageEditor: React.FC = () => {
  const { toast } = useToast();
  const { t } = useLanguage();
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Store preview width in ref to avoid forced reflow on export
  const previewWidthRef = useRef<number>(500);
  const [activeCellId, setActiveCellId] = useState<string | null>(null);
  const [editingCellId, setEditingCellId] = useState<string | null>(null);
  const [downloadFormat, setDownloadFormat] = useState<'png' | 'jpg'>('png');
  
  const [selectedTemplate, setSelectedTemplate] = useState<Template>(TEMPLATES[0]);
  const [cells, setCells] = useState<CellData[]>(
    TEMPLATES[0].cells.map(c => ({ ...c, image: null, transform: { ...defaultTransform } }))
  );
  
  // History for each cell
  const [cellHistory, setCellHistory] = useState<Record<string, HistoryState>>({});
  
  // Canvas settings
  const [canvasWidth, setCanvasWidth] = useState(1000);
  const [canvasHeight, setCanvasHeight] = useState(1000);
  const [gap, setGap] = useState(4);
  const [borderRadius, setBorderRadius] = useState(0);
  const [canvasBorder, setCanvasBorder] = useState(4);
  const [cellBorder, setCellBorder] = useState(0);
  const [borderColor, setBorderColor] = useState('#000000');
  const [backgroundColor, setBackgroundColor] = useState('#0f172a');
  const [backgroundImage, setBackgroundImage] = useState<string | null>(null);
  const [selectedFilter, setSelectedFilter] = useState('none');
  
  // Image adjustments
  const [adjustments, setAdjustments] = useState<ImageAdjustments>({ ...defaultAdjustments });
  
  // Text items
  const [textItems, setTextItems] = useState<TextItem[]>([]);
  
  // Sticker library (uploaded stickers - persist through reset)
  const [stickerLibrary, setStickerLibrary] = useState<{ id: string; image: string }[]>([]);
  
  // Sticker items on canvas (cleared on reset)
  const [stickerItems, setStickerItems] = useState<StickerItem[]>([]);
  
  // Text size for sidebar
  const [textSize, setTextSize] = useState<TextSize>('medium');

  // Check if user has added content (images or text or stickers)
  const hasContent = cells.some(cell => cell.image !== null) || textItems.length > 0 || stickerItems.length > 0;

  const saveToHistory = useCallback((cellId: string, transform: ImageTransform) => {
    setCellHistory(prev => ({
      ...prev,
      [cellId]: {
        past: [...(prev[cellId]?.past || []), transform],
        future: [],
      },
    }));
  }, []);

  const handleTemplateSelect = useCallback((template: Template) => {
    // Revoke old blob URLs to prevent memory leaks
    cells.forEach(cell => {
      if (cell.image && cell.image.startsWith('blob:')) {
        URL.revokeObjectURL(cell.image);
      }
    });
    setSelectedTemplate(template);
    setCells(template.cells.map(c => ({ ...c, image: null, originalFile: undefined, transform: { ...defaultTransform } })));
    setCellHistory({});
    setEditingCellId(null);
  }, [cells]);

  const handleGenerateTemplate = useCallback((rows: number, cols: number) => {
    const newCells = Array.from({ length: rows * cols }, (_, i) => ({
      id: String(i + 1),
      image: null,
      transform: { ...defaultTransform },
    }));
    setSelectedTemplate({
      id: 'custom',
      name: `${rows}×${cols}`,
      columns: cols,
      cells: newCells,
      preview: null,
    });
    setCells(newCells);
    setCellHistory({});
    setEditingCellId(null);
    // Показываем уведомление только один раз
    const TEMPLATE_TOAST_KEY = 'collage_template_toast_shown';
    try {
      if (localStorage.getItem(TEMPLATE_TOAST_KEY) !== 'true') {
        toast({
          title: t.templateCreated,
          description: `${t.gridReady} ${rows}×${cols}`,
        });
        localStorage.setItem(TEMPLATE_TOAST_KEY, 'true');
      }
    } catch {}
  }, [toast, t]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && activeCellId) {
      // Use createObjectURL for instant preview (no blocking)
      const objectUrl = URL.createObjectURL(file);
      const cellId = activeCellId;
      
      // Load image to get dimensions
      const img = new Image();
      img.onload = () => {
        const aspectRatio = img.width / img.height;
        
        setCells(prev => 
          prev.map(cell => {
            if (cell.id === cellId) {
              // Revoke old URL to prevent memory leak
              if (cell.image && cell.image.startsWith('blob:')) {
                URL.revokeObjectURL(cell.image);
              }
              return { 
                ...cell, 
                image: objectUrl, 
                originalFile: file,
                transform: { ...defaultTransform, aspectRatio }
              };
            }
            return cell;
          })
        );
      };
      img.src = objectUrl;
    }
    e.target.value = '';
  }, [activeCellId]);

  const handleImageDrop = useCallback((cellId: string, file: File) => {
    // Use createObjectURL for instant preview (no blocking)
    const objectUrl = URL.createObjectURL(file);
    
    // Load image to get dimensions
    const img = new Image();
    img.onload = () => {
      const aspectRatio = img.width / img.height;
      
      setCells(prev => 
        prev.map(cell => {
          if (cell.id === cellId) {
            // Revoke old URL to prevent memory leak
            if (cell.image && cell.image.startsWith('blob:')) {
              URL.revokeObjectURL(cell.image);
            }
            return { 
              ...cell, 
              image: objectUrl, 
              originalFile: file,
              transform: { ...defaultTransform, aspectRatio }
            };
          }
          return cell;
        })
      );
    };
    img.src = objectUrl;
  }, []);

  const handleRemoveImage = useCallback((cellId: string) => {
    setCells(prev => 
      prev.map(cell => {
        if (cell.id === cellId) {
          // Revoke blob URL to prevent memory leak
          if (cell.image && cell.image.startsWith('blob:')) {
            URL.revokeObjectURL(cell.image);
          }
          return { ...cell, image: null, originalFile: undefined, transform: { ...defaultTransform } };
        }
        return cell;
      })
    );
    setCellHistory(prev => ({ ...prev, [cellId]: { past: [], future: [] } }));
    setEditingCellId(null);
  }, []);

  const handleCellClick = useCallback((cellId: string) => {
    setActiveCellId(cellId);
    fileInputRef.current?.click();
  }, []);

  const handleEditCell = useCallback((cellId: string | null) => {
    setEditingCellId(cellId);
  }, []);

  const handleReplaceImage = useCallback((cellId: string) => {
    setActiveCellId(cellId);
    fileInputRef.current?.click();
  }, []);

  const handleFlipH = useCallback((cellId: string) => {
    setCells(prev => 
      prev.map(cell => {
        if (cell.id === cellId && cell.transform) {
          saveToHistory(cellId, cell.transform);
          return { ...cell, transform: { ...cell.transform, flipH: !cell.transform.flipH } };
        }
        return cell;
      })
    );
  }, [saveToHistory]);

  const handleFlipV = useCallback((cellId: string) => {
    setCells(prev => 
      prev.map(cell => {
        if (cell.id === cellId && cell.transform) {
          saveToHistory(cellId, cell.transform);
          return { ...cell, transform: { ...cell.transform, flipV: !cell.transform.flipV } };
        }
        return cell;
      })
    );
  }, [saveToHistory]);

  const handleUndo = useCallback((cellId: string) => {
    const history = cellHistory[cellId];
    if (!history || history.past.length === 0) return;

    const currentCell = cells.find(c => c.id === cellId);
    if (!currentCell?.transform) return;

    const previousTransform = history.past[history.past.length - 1];
    
    setCellHistory(prev => ({
      ...prev,
      [cellId]: {
        past: prev[cellId].past.slice(0, -1),
        future: [currentCell.transform!, ...prev[cellId].future],
      },
    }));

    setCells(prev => 
      prev.map(cell => 
        cell.id === cellId ? { ...cell, transform: previousTransform } : cell
      )
    );
  }, [cellHistory, cells]);

  const handleRedo = useCallback((cellId: string) => {
    const history = cellHistory[cellId];
    if (!history || history.future.length === 0) return;

    const currentCell = cells.find(c => c.id === cellId);
    if (!currentCell?.transform) return;

    const nextTransform = history.future[0];
    
    setCellHistory(prev => ({
      ...prev,
      [cellId]: {
        past: [...prev[cellId].past, currentCell.transform!],
        future: prev[cellId].future.slice(1),
      },
    }));

    setCells(prev => 
      prev.map(cell => 
        cell.id === cellId ? { ...cell, transform: nextTransform } : cell
      )
    );
  }, [cellHistory, cells]);

  const canUndo = useCallback((cellId: string) => {
    return (cellHistory[cellId]?.past.length || 0) > 0;
  }, [cellHistory]);

  const canRedo = useCallback((cellId: string) => {
    return (cellHistory[cellId]?.future.length || 0) > 0;
  }, [cellHistory]);

  const handleSetImageScale = useCallback((cellId: string, newScale: number) => {
    setCells(prev => 
      prev.map(cell => {
        if (cell.id === cellId) {
          const currentTransform = cell.transform || { scale: 1, offsetX: 0, offsetY: 0, flipH: false, flipV: false };
          return { 
            ...cell, 
            transform: { 
              ...currentTransform, 
              scale: newScale,
            } 
          };
        }
        return cell;
      })
    );
  }, []);

  const handleMoveImageDrag = useCallback((cellId: string, offsetX: number, offsetY: number) => {
    setCells(prev => 
      prev.map(cell => {
        if (cell.id === cellId) {
          const currentTransform = cell.transform || { scale: 1, offsetX: 0, offsetY: 0, flipH: false, flipV: false };
          return { 
            ...cell, 
            transform: { 
              ...currentTransform, 
              offsetX,
              offsetY,
            } 
          };
        }
        return cell;
      })
    );
  }, []);

  const handleAddText = useCallback(() => {
    const newId = `text-${Date.now()}`;
    const newTextItem: TextItem = {
      id: newId,
      text: t.newText,
      font: 'Impact',
      size: 32,
      color: '#ffffff',
      x: 50,
      y: 50,
      shadow: true,
      stroke: true,
      strokeColor: '#000000',
      strokeWidth: 2,
      textAlign: 'center',
      boxWidth: 200,
    };
    setTextItems(prev => [...prev, newTextItem]);
    return newId;
  }, [t.newText]);

  const handleUpdateText = useCallback((id: string, updates: Partial<TextItem>) => {
    setTextItems(prev => 
      prev.map(item => item.id === id ? { ...item, ...updates } : item)
    );
  }, []);

  const handleRemoveText = useCallback((id: string) => {
    setTextItems(prev => prev.filter(item => item.id !== id));
  }, []);

  const handleTextDrag = useCallback((id: string, x: number, y: number) => {
    setTextItems(prev => 
      prev.map(item => item.id === id ? { ...item, x, y } : item)
    );
  }, []);

  // Sticker handlers
  // Add sticker to library only (not to canvas)
  const handleAddStickerToLibrary = useCallback((file: File) => {
    const newId = `sticker-${Date.now()}`;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      setStickerLibrary(prev => [...prev, { id: newId, image: dataUrl }]);
    };
    reader.readAsDataURL(file);
  }, []);

  // Place sticker from library onto canvas
  const handlePlaceStickerOnCanvas = useCallback((libraryStickerId: string) => {
    const libraryItem = stickerLibrary.find(s => s.id === libraryStickerId);
    if (!libraryItem) return;
    
    const newSticker: StickerItem = {
      id: `sticker-canvas-${Date.now()}`,
      image: libraryItem.image,
      x: 50,
      y: 50,
      scale: 1,
      rotation: 0,
    };
    setStickerItems(prev => [...prev, newSticker]);
  }, [stickerLibrary]);

  // Remove sticker from library
  const handleRemoveStickerFromLibrary = useCallback((id: string) => {
    setStickerLibrary(prev => prev.filter(item => item.id !== id));
  }, []);

  const handleUpdateSticker = useCallback((id: string, updates: Partial<StickerItem>) => {
    setStickerItems(prev => 
      prev.map(item => item.id === id ? { ...item, ...updates } : item)
    );
  }, []);

  const handleRemoveSticker = useCallback((id: string) => {
    setStickerItems(prev => prev.filter(item => item.id !== id));
  }, []);

  const handleStickerDrag = useCallback((id: string, x: number, y: number) => {
    setStickerItems(prev => 
      prev.map(item => item.id === id ? { ...item, x, y } : item)
    );
  }, []);

  const handleReset = useCallback(() => {
    // Revoke old blob URLs to prevent memory leaks
    cells.forEach(cell => {
      if (cell.image && cell.image.startsWith('blob:')) {
        URL.revokeObjectURL(cell.image);
      }
    });
    // Reset cells, text and stickers on canvas - but keep sticker library
    setCells(selectedTemplate.cells.map(c => ({ ...c, image: null, originalFile: undefined })));
    setTextItems([]);
    setStickerItems([]); // Only clear stickers from canvas, library stays
    setSelectedFilter('none');
    // Показываем уведомление только один раз
    const RESET_TOAST_KEY = 'collage_reset_toast_shown';
    try {
      if (localStorage.getItem(RESET_TOAST_KEY) !== 'true') {
        toast({
          title: t.resetDone,
          description: t.allChangesReverted,
        });
        localStorage.setItem(RESET_TOAST_KEY, 'true');
      }
    } catch {}
  }, [selectedTemplate, toast, t, cells, stickerItems]);

  // Load image as HTMLImageElement from URL or File with timeout
  // For Files, we convert to base64 data URL to avoid CORS issues on mobile
  const loadImage = useCallback((src: string | File): Promise<HTMLImageElement> => {
    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        reject(new Error('Image load timeout'));
      }, 10000);
      
      const createImage = (imageUrl: string) => {
        const img = new Image();
        // Don't set crossOrigin for data URLs or blob URLs
        if (!imageUrl.startsWith('data:') && !imageUrl.startsWith('blob:')) {
          img.crossOrigin = 'anonymous';
        }
        
        img.onload = () => {
          clearTimeout(timeoutId);
          resolve(img);
        };
        
        img.onerror = () => {
          clearTimeout(timeoutId);
          reject(new Error('Image load failed'));
        };
        
        img.src = imageUrl;
      };
      
      if (src instanceof File) {
        // Convert File to base64 data URL to avoid CORS issues on mobile
        const reader = new FileReader();
        reader.onload = () => {
          createImage(reader.result as string);
        };
        reader.onerror = () => {
          clearTimeout(timeoutId);
          reject(new Error('File read failed'));
        };
        reader.readAsDataURL(src);
      } else {
        createImage(src);
      }
    });
  }, []);

  /**
   * ПОДХОД КОНКУРЕНТА для экспорта:
   * 
   * offsetX/offsetY - смещение от центра в процентах от размера ячейки
   * Изображение рисуется с вычисленными размерами и позицией
   */
  const drawImageCover = useCallback((
    ctx: CanvasRenderingContext2D,
    img: HTMLImageElement,
    x: number,
    y: number,
    width: number,
    height: number,
    transform?: { scale?: number; offsetX?: number; offsetY?: number; flipH?: boolean; flipV?: boolean; aspectRatio?: number }
  ) => {
    const scale = transform?.scale || 1;
    const offsetX = transform?.offsetX || 0;
    const offsetY = transform?.offsetY || 0;
    const flipH = transform?.flipH || false;
    const flipV = transform?.flipV || false;

    ctx.save();
    
    // Create clipping region for this cell
    ctx.beginPath();
    ctx.rect(x, y, width, height);
    ctx.clip();

    // Calculate dimensions for cover behavior
    // ВАЖНО: используем сохранённый aspectRatio из transform если есть,
    // чтобы гарантировать идентичность с превью
    const imgRatio = transform?.aspectRatio || (img.width / img.height);
    const cellRatio = width / height;
    
    let baseWidth: number, baseHeight: number;
    
    if (imgRatio > cellRatio) {
      baseHeight = height;
      baseWidth = baseHeight * imgRatio;
    } else {
      baseWidth = width;
      baseHeight = baseWidth / imgRatio;
    }

    // Apply scale
    const drawWidth = baseWidth * scale;
    const drawHeight = baseHeight * scale;

    // Центрированная позиция
    const centerX = (width - drawWidth) / 2;
    const centerY = (height - drawHeight) / 2;
    
    // Смещение в пикселях
    const pixelOffsetX = (offsetX / 100) * width;
    const pixelOffsetY = (offsetY / 100) * height;
    
    const posX = x + centerX + pixelOffsetX;
    const posY = y + centerY + pixelOffsetY;

    // Apply flip
    if (flipH || flipV) {
      const flipCenterX = x + width / 2;
      const flipCenterY = y + height / 2;
      ctx.translate(flipCenterX, flipCenterY);
      ctx.scale(flipH ? -1 : 1, flipV ? -1 : 1);
      ctx.translate(-flipCenterX, -flipCenterY);
    }

    ctx.drawImage(img, posX, posY, drawWidth, drawHeight);
    
    ctx.restore();
  }, []);

  const handleDownload = useCallback(async () => {
    // Показываем уведомление о подготовке только один раз
    const PREPARING_TOAST_KEY = 'collage_preparing_toast_shown';
    try {
      if (localStorage.getItem(PREPARING_TOAST_KEY) !== 'true') {
        toast({
          title: t.preparing,
          description: t.creatingCollage,
        });
        localStorage.setItem(PREPARING_TOAST_KEY, 'true');
      }
    } catch {}

    try {
      console.log('Starting download...');
      
      // Create high-resolution canvas
      const canvas = document.createElement('canvas');
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;
      const ctx = canvas.getContext('2d', { alpha: true });
      
      if (!ctx) {
        console.error('Canvas context not available');
        throw new Error('Canvas context not available');
      }
      
      console.log('Canvas created:', canvasWidth, 'x', canvasHeight);

      // Enable high quality rendering
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      // Apply global filter (brightness, contrast, saturation)
      const filterStyle = FILTERS.find(f => f.id === selectedFilter)?.filter || '';
      const adjustmentStyle = `brightness(${adjustments.brightness / 100}) contrast(${adjustments.contrast / 100}) saturate(${adjustments.saturation / 100})`;
      const combinedFilter = [filterStyle, adjustmentStyle].filter(Boolean).join(' ');
      if (combinedFilter) {
        ctx.filter = combinedFilter;
      }

      // Calculate scale factor based on ACTUAL preview size vs export canvas size
      // Use cached previewWidthRef to avoid forced reflow (reading offsetWidth)
      const actualPreviewWidth = previewWidthRef.current || 500;
      const scaleFactor = canvasWidth / actualPreviewWidth;
      
      // Scale all settings proportionally
      const scaledGap = gap * scaleFactor;
      const scaledBorderRadius = borderRadius * scaleFactor;
      const scaledCanvasBorder = canvasBorder * scaleFactor;
      const scaledCellBorder = cellBorder * scaleFactor;

      // Draw border color as base (this fills gaps and canvas border)
      ctx.fillStyle = borderColor;
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);

      // Calculate grid dimensions with scaled values
      const innerWidth = canvasWidth - scaledCanvasBorder * 2;
      const innerHeight = canvasHeight - scaledCanvasBorder * 2;
      const columns = selectedTemplate.columns;
      
      // Calculate actual number of rows based on cell spans (like CSS grid does)
      // Build a grid occupation map to match CSS grid layout
      const gridMap: (string | null)[][] = [];
      let maxRow = 0;
      
      for (const cell of cells) {
        const spanCol = cell.span?.col || 1;
        const spanRow = cell.span?.row || 1;
        
        // Find first available position for this cell
        let placed = false;
        for (let r = 0; !placed; r++) {
          // Ensure row exists in gridMap
          while (gridMap.length <= r + spanRow - 1) {
            gridMap.push(new Array(columns).fill(null));
          }
          
          for (let c = 0; c <= columns - spanCol; c++) {
            // Check if all required cells are free
            let canPlace = true;
            for (let dr = 0; dr < spanRow && canPlace; dr++) {
              for (let dc = 0; dc < spanCol && canPlace; dc++) {
                if (gridMap[r + dr][c + dc] !== null) {
                  canPlace = false;
                }
              }
            }
            
            if (canPlace) {
              // Place this cell
              for (let dr = 0; dr < spanRow; dr++) {
                for (let dc = 0; dc < spanCol; dc++) {
                  gridMap[r + dr][c + dc] = cell.id;
                }
              }
              maxRow = Math.max(maxRow, r + spanRow);
              placed = true;
              break;
            }
          }
        }
      }
      
      const rows = maxRow || Math.ceil(cells.length / columns);
      const totalGapX = scaledGap * (columns - 1);
      const totalGapY = scaledGap * (rows - 1);
      const cellWidth = (innerWidth - totalGapX) / columns;
      const cellHeight = (innerHeight - totalGapY) / rows;

      // Draw each cell
      const drawnCells = new Set<string>();
      
      for (let r = 0; r < gridMap.length; r++) {
        for (let c = 0; c < columns; c++) {
          const cellId = gridMap[r][c];
          if (!cellId || drawnCells.has(cellId)) continue;
          
          const cell = cells.find(ce => ce.id === cellId);
          if (!cell) continue;
          
          drawnCells.add(cellId);
          
          const spanCol = cell.span?.col || 1;
          const spanRow = cell.span?.row || 1;
          
          // Calculate cell position based on grid position
          const x = scaledCanvasBorder + c * (cellWidth + scaledGap);
          const y = scaledCanvasBorder + r * (cellHeight + scaledGap);
          
          // Handle cell spans
          const actualWidth = cellWidth * spanCol + scaledGap * (spanCol - 1);
          const actualHeight = cellHeight * spanRow + scaledGap * (spanRow - 1);

          // Draw cell border if needed (scaled)
          if (scaledCellBorder > 0) {
            ctx.strokeStyle = borderColor;
            ctx.lineWidth = scaledCellBorder;
            ctx.strokeRect(x, y, actualWidth, actualHeight);
          }

          // Draw cell background
          ctx.save();
          if (scaledBorderRadius > 0) {
            ctx.beginPath();
            ctx.roundRect(x, y, actualWidth, actualHeight, scaledBorderRadius);
            ctx.clip();
          }
          
          // Draw background color/image for cell
          if (backgroundImage) {
            const bgImg = await loadImage(backgroundImage);
            ctx.drawImage(bgImg, x, y, actualWidth, actualHeight);
          } else {
            ctx.fillStyle = backgroundColor;
            ctx.fillRect(x, y, actualWidth, actualHeight);
          }
          
          // Draw image if present - use preview URL (more reliable on mobile)
          if (cell.image) {
            console.log('Loading image for cell:', cell.id);
            try {
              const img = await loadImage(cell.image);
              console.log('Image loaded successfully:', img.width, 'x', img.height);
              drawImageCover(ctx, img, x, y, actualWidth, actualHeight, cell.transform);
            } catch (imgError) {
              console.error('Failed to load image for cell:', cell.id, imgError);
              // Don't throw - try to continue with other cells
            }
          }
          
          ctx.restore();
        }
      }

      // Draw text overlays
      // In preview, text is positioned inside the canvas element which has padding = canvasBorder
      // The text position (x, y) is relative to the full canvas including padding
      // So we need to calculate position the same way: percentage of full canvas size
      for (const textItem of textItems) {
        // Skip empty text
        if (!textItem.text || textItem.text.trim().length === 0) continue;
        
        // Apply same clamping as in preview (10-90%)
        const clampedX = Math.max(10, Math.min(90, textItem.x));
        const clampedY = Math.max(10, Math.min(90, textItem.y));
        const textX = (clampedX / 100) * canvasWidth;
        const textY = (clampedY / 100) * canvasHeight;
        
        // Use scaleFactor (based on actual preview width) for accurate font scaling
        const fontSize = textItem.size * scaleFactor;
        const font = `${fontSize}px ${textItem.font}`;
        ctx.font = font;
        ctx.fillStyle = textItem.color;
        // Always use center alignment for canvas - text block is positioned at center
        // Internal text alignment is handled by measuring each line
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        
        if (textItem.shadow) {
          ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
          ctx.shadowBlur = 4 * scaleFactor;
          ctx.shadowOffsetX = 2 * scaleFactor;
          ctx.shadowOffsetY = 2 * scaleFactor;
        }
        
        // Support multi-line text
        const lines = textItem.text.split('\n');
        const lineHeight = fontSize * 1.2;
        const totalHeight = (lines.length - 1) * lineHeight;
        const startY = textY - totalHeight / 2;
        
        // Setup stroke if enabled
        // CSS -webkit-text-stroke with paint-order: stroke fill renders stroke UNDER fill
        // Only the outer half of the stroke is visible (inner half covered by fill)
        // Canvas: draw stroke first, then fill on top - same visual result
        const hasStroke = textItem.stroke && textItem.strokeWidth > 0;
        if (hasStroke) {
          ctx.strokeStyle = textItem.strokeColor;
          // Scale stroke width proportionally to canvas size
          ctx.lineWidth = textItem.strokeWidth * scaleFactor;
          ctx.lineJoin = 'round';
          ctx.lineCap = 'round';
          ctx.miterLimit = 2;
        }
        
        // Calculate max line width to determine text block bounds
        const lineWidths = lines.map(line => ctx.measureText(line).width);
        const maxLineWidth = Math.max(...lineWidths);
        
        // Text block is centered at textX, textY
        // For alignment within the block:
        // - center: each line is centered at textX
        // - left: each line starts at textX - maxLineWidth/2
        // - right: each line ends at textX + maxLineWidth/2
        const textAlign = textItem.textAlign || 'center';
        
        lines.forEach((line, index) => {
          const y = startY + index * lineHeight;
          const lineWidth = lineWidths[index];
          
          let lineX: number;
          if (textAlign === 'left') {
            lineX = textX - maxLineWidth / 2 + lineWidth / 2;
          } else if (textAlign === 'right') {
            lineX = textX + maxLineWidth / 2 - lineWidth / 2;
          } else {
            lineX = textX;
          }
          
          // Draw stroke first (underneath), then fill on top
          // This matches CSS paint-order: stroke fill
          if (hasStroke) {
            ctx.strokeText(line, lineX, y);
          }
          ctx.fillText(line, lineX, y);
        });
        
        ctx.shadowColor = 'transparent';
      }

      // Draw sticker overlays
      for (const sticker of stickerItems) {
        try {
          const stickerImg = await loadImage(sticker.image);
          
          // Sticker position is percentage of full canvas (including border padding)
          // This matches the CSS positioning in CollageGrid which uses left/top %
          const stickerX = (sticker.x / 100) * canvasWidth;
          const stickerY = (sticker.y / 100) * canvasHeight;
          
          // Base size is 100px in preview, scale it proportionally
          const baseSize = 100 * scaleFactor;
          
          // Calculate draw dimensions maintaining aspect ratio
          const imgAspectRatio = stickerImg.width / stickerImg.height;
          let drawWidth: number;
          let drawHeight: number;
          
          if (imgAspectRatio >= 1) {
            // Wider than tall - width is the limiting factor
            drawWidth = baseSize;
            drawHeight = baseSize / imgAspectRatio;
          } else {
            // Taller than wide - height is the limiting factor
            drawHeight = baseSize;
            drawWidth = baseSize * imgAspectRatio;
          }
          
          // Apply sticker scale
          drawWidth *= sticker.scale;
          drawHeight *= sticker.scale;
          
          ctx.save();
          ctx.translate(stickerX, stickerY);
          ctx.rotate((sticker.rotation * Math.PI) / 180);
          ctx.drawImage(stickerImg, -drawWidth / 2, -drawHeight / 2, drawWidth, drawHeight);
          ctx.restore();
        } catch (err) {
          console.error('Failed to draw sticker:', err);
        }
      }

      // Reset filter for export
      ctx.filter = 'none';

      // Convert canvas to blob and download
      const mimeType = downloadFormat === 'jpg' ? 'image/jpeg' : 'image/png';
      const filename = `CollageEditor.com-${Date.now()}.${downloadFormat}`;
      
      // Check if we're on mobile
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || window.innerWidth < 768;
      
      canvas.toBlob(async (blob) => {
        try {
          if (!blob) {
            console.error('Canvas toBlob returned null');
            toast({
              title: t.error,
              description: t.couldNotSave,
              variant: 'destructive',
            });
            return;
          }
          
          // Create blob URL for download
          const blobUrl = URL.createObjectURL(blob);
          
          // Try direct download first
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          // On mobile: also open Share sheet for additional options
          if (isMobile && navigator.share && navigator.canShare) {
            try {
              const file = new File([blob], filename, { type: mimeType });
              if (navigator.canShare({ files: [file] })) {
                await navigator.share({
                  files: [file],
                  title: 'Collage',
                });
              }
            } catch (shareError) {
              // Ignore share errors - download already happened
              console.log('Share cancelled or failed');
            }
          }
          
          setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
          
          // Показываем уведомление об успехе только один раз
          const DOWNLOAD_SUCCESS_SHOWN_KEY = 'collage_download_success_shown';
          try {
            if (localStorage.getItem(DOWNLOAD_SUCCESS_SHOWN_KEY) !== 'true') {
              toast({
                title: t.done,
                description: `${t.collageSaved} ${downloadFormat.toUpperCase()}`,
              });
              localStorage.setItem(DOWNLOAD_SUCCESS_SHOWN_KEY, 'true');
            }
          } catch {
            // Если localStorage недоступен, показываем уведомление
            toast({
              title: t.done,
              description: `${t.collageSaved} ${downloadFormat.toUpperCase()}`,
            });
          }
        } catch (blobError) {
          console.error('Error in toBlob callback:', blobError);
          toast({
            title: t.error,
            description: t.couldNotSave,
            variant: 'destructive',
          });
        }
      }, mimeType, 0.95);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('Download error:', errorMessage, error);
      toast({
        title: t.error,
        description: `${t.couldNotSave}: ${errorMessage}`,
        variant: 'destructive',
      });
    }
  }, [canvasWidth, canvasHeight, downloadFormat, toast, t, cells, selectedTemplate, gap, borderRadius, canvasBorder, cellBorder, borderColor, backgroundColor, backgroundImage, selectedFilter, adjustments, textItems, stickerItems, loadImage, drawImageCover]);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SEOHead />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif,image/*"
        className="hidden"
        onChange={handleFileSelect}
        aria-label="Upload image file"
      />

      {/* Header */}
      <header className="glass border-b border-border/50 sticky top-0 z-50">
        <div className="container mx-auto px-2 sm:px-4 py-2 sm:py-3">
          <div className="flex items-center justify-between gap-2">
            {/* Logo */}
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br from-neon-cyan via-neon-purple to-neon-pink flex items-center justify-center shadow-[0_0_20px_hsl(190_100%_50%/0.4)]">
                <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-primary-foreground" />
              </div>
              <div>
                <h1 className="text-base sm:text-xl font-bold neon-text">{t.headerTitle}</h1>
                <p className="hidden sm:block text-xs text-muted-foreground">{t.headerSubtitle}</p>
              </div>
            </div>

            {/* Controls */}
            <div className="flex items-center gap-1 sm:gap-2">
              <LanguageSwitcher />
              <ThemeToggle />
              <Button variant="ghost" size="icon" className="h-8 w-8 sm:h-9 sm:w-auto sm:px-3" onClick={handleReset} aria-label="Reset collage">
                <RotateCcw className="w-4 h-4" />
                <span className="hidden sm:inline sm:ml-1">{t.reset}</span>
              </Button>
              
              <div className="hidden lg:flex items-center gap-2">
                <div className="flex rounded-md border border-input overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setDownloadFormat('png')}
                    aria-label="Select PNG format"
                    aria-pressed={downloadFormat === 'png'}
                    className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                      downloadFormat === 'png'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-background text-foreground hover:bg-muted'
                    }`}
                  >
                    PNG
                  </button>
                  <button
                    type="button"
                    onClick={() => setDownloadFormat('jpg')}
                    aria-label="Select JPG format"
                    aria-pressed={downloadFormat === 'jpg'}
                    className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                      downloadFormat === 'jpg'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-background text-foreground hover:bg-muted'
                    }`}
                  >
                    JPG
                  </button>
                </div>

                <Button variant="neon" size="default" onClick={handleDownload}>
                  <Download className="w-4 h-4 mr-1" />
                  {t.download}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-4 md:py-6 overflow-visible flex-1">
        <div className="grid lg:grid-cols-[280px_1fr] xl:grid-cols-[320px_1fr] gap-4 md:gap-6 overflow-visible">
          {/* Sidebar */}
          <aside className={`glass rounded-2xl p-3 md:p-4 h-fit animate-slide-in-left order-2 lg:order-1 panel-sidebar text-size-${textSize}`}>
            <Tabs defaultValue="templates" className="w-full">
              <TabsList className="grid grid-cols-7 mb-3 md:mb-4">
                <TabsTrigger value="templates" className="text-xs px-1 md:px-2" aria-label="Templates">
                  <Grid className="w-4 h-4" />
                </TabsTrigger>
                <TabsTrigger value="settings" className="text-xs px-1 md:px-2" aria-label="Settings">
                  <Sliders className="w-4 h-4" />
                </TabsTrigger>
                <TabsTrigger value="adjustments" className="text-xs px-1 md:px-2" aria-label="Adjustments">
                  <SunMedium className="w-4 h-4" />
                </TabsTrigger>
                <TabsTrigger value="text" className="text-xs px-1 md:px-2" aria-label="Text">
                  <Type className="w-4 h-4" />
                </TabsTrigger>
                <TabsTrigger value="stickers" className="text-xs px-1 md:px-2" aria-label="Stickers">
                  <Sticker className="w-4 h-4" />
                </TabsTrigger>
                <TabsTrigger value="background" className="text-xs px-1 md:px-2" aria-label="Background">
                  <Palette className="w-4 h-4" />
                </TabsTrigger>
                <TabsTrigger value="filters" className="text-xs px-1 md:px-2" aria-label="Filters">
                  <ImageIcon className="w-4 h-4" />
                </TabsTrigger>
              </TabsList>

              <TabsContent value="templates" className="space-y-3 md:space-y-4 mt-0">
                <TemplateSelector
                  selectedTemplate={selectedTemplate.id}
                  onSelectTemplate={handleTemplateSelect}
                />
                <div className="border-t border-border/50 pt-3 md:pt-4">
                  <TemplateGenerator onGenerateTemplate={handleGenerateTemplate} />
                </div>
                <div className="border-t border-border/50 pt-3 md:pt-4">
                  <SizeSelector
                    width={canvasWidth}
                    height={canvasHeight}
                    onSizeChange={(w, h) => { setCanvasWidth(w); setCanvasHeight(h); }}
                  />
                </div>
              </TabsContent>

              <TabsContent value="settings" className="mt-0">
                <SettingsPanel
                  gap={gap}
                  borderRadius={borderRadius}
                  canvasBorder={canvasBorder}
                  cellBorder={cellBorder}
                  borderColor={borderColor}
                  onGapChange={setGap}
                  onBorderRadiusChange={setBorderRadius}
                  onCanvasBorderChange={setCanvasBorder}
                  onCellBorderChange={setCellBorder}
                  onBorderColorChange={setBorderColor}
                />
              </TabsContent>

              <TabsContent value="adjustments" className="mt-0">
                <AdjustmentsPanel
                  adjustments={adjustments}
                  onAdjustmentsChange={setAdjustments}
                  onReset={() => setAdjustments({ ...defaultAdjustments })}
                />
              </TabsContent>

              <TabsContent value="text" className="mt-0">
                <TextOverlay
                  textItems={textItems}
                  onAddText={handleAddText}
                  onUpdateText={handleUpdateText}
                  onRemoveText={handleRemoveText}
                />
              </TabsContent>

              <TabsContent value="stickers" className="mt-0">
                <StickerOverlay
                  stickerLibrary={stickerLibrary}
                  stickersOnCanvas={stickerItems}
                  onAddStickerToLibrary={handleAddStickerToLibrary}
                  onPlaceStickerOnCanvas={handlePlaceStickerOnCanvas}
                  onRemoveStickerFromLibrary={handleRemoveStickerFromLibrary}
                  onUpdateSticker={handleUpdateSticker}
                  onRemoveSticker={handleRemoveSticker}
                />
              </TabsContent>

              <TabsContent value="background" className="mt-0">
                <BackgroundPanel
                  backgroundColor={backgroundColor}
                  backgroundImage={backgroundImage}
                  onBackgroundColorChange={setBackgroundColor}
                  onBackgroundImageChange={setBackgroundImage}
                />
              </TabsContent>

              <TabsContent value="filters" className="mt-0">
                <FiltersPanel
                  selectedFilter={selectedFilter}
                  onSelectFilter={setSelectedFilter}
                />
              </TabsContent>
            </Tabs>
          </aside>

          {/* Text Size Selector - отдельный блок */}
          <div className="order-3 lg:order-3 w-full lg:w-auto p-4 rounded-lg border border-border bg-card/50">
            <TextSizeSelector textSize={textSize} onTextSizeChange={setTextSize} />
          </div>

          {/* Canvas Area */}
          <section className="animate-fade-in flex flex-col items-center overflow-visible order-1 lg:order-2">
            <div className="w-full max-w-[450px] overflow-visible p-4 sm:p-8 md:p-12">
              <CollageGrid
                  template={cells}
                  columns={selectedTemplate.columns}
                  gap={gap}
                  borderRadius={borderRadius}
                  backgroundColor={backgroundColor}
                  backgroundImage={backgroundImage}
                  canvasBorder={canvasBorder}
                  cellBorder={cellBorder}
                  borderColor={borderColor}
                  canvasWidth={canvasWidth}
                  canvasHeight={canvasHeight}
                  filter={selectedFilter}
                  adjustments={adjustments}
                  textItems={textItems}
                  stickerItems={stickerItems}
                  editingCellId={editingCellId}
                  onCellClick={handleCellClick}
                  onImageDrop={handleImageDrop}
                  onRemoveImage={handleRemoveImage}
                  onTextDrag={handleTextDrag}
                  onStickerDrag={handleStickerDrag}
                  onEditCell={handleEditCell}
                  onReplaceImage={handleReplaceImage}
                  onFlipH={handleFlipH}
                  onFlipV={handleFlipV}
                  onUndo={handleUndo}
                  onRedo={handleRedo}
                  canUndo={canUndo}
                  canRedo={canRedo}
                  onSetImageScale={handleSetImageScale}
                  onMoveImageDrag={handleMoveImageDrag}
                  onUpdateText={handleUpdateText}
                  onRemoveText={handleRemoveText}
                  onRemoveSticker={handleRemoveSticker}
                  onUpdateSticker={handleUpdateSticker}
                  onPreviewResize={(width) => { previewWidthRef.current = width; }}
                />
            </div>

            <p className="text-center text-xs sm:text-sm text-muted-foreground mt-2 md:mt-4 px-4">
              {t.clickOrDragHint}
            </p>

            {/* Spacer for editing toolbar on mobile */}
            <div className={`lg:hidden transition-all duration-200 ${editingCellId ? 'h-14' : 'h-0'}`} />

            {/* Mobile Download Button - shown below canvas on mobile only when content exists */}
            {hasContent && (
              <div 
                id="mobile-download-block"
                className={`flex lg:hidden items-center justify-center gap-2 mt-4 w-full px-4 animate-fade-in transition-opacity ${
                  editingCellId ? 'opacity-50 pointer-events-none' : ''
                }`}
              >
                <div className="flex rounded-md border border-input overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setDownloadFormat('png')}
                    aria-label="Select PNG format"
                    aria-pressed={downloadFormat === 'png'}
                    className={`px-3 py-2 text-sm font-medium transition-colors ${
                      downloadFormat === 'png'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-background text-foreground hover:bg-muted'
                    }`}
                  >
                    PNG
                  </button>
                  <button
                    type="button"
                    onClick={() => setDownloadFormat('jpg')}
                    aria-label="Select JPG format"
                    aria-pressed={downloadFormat === 'jpg'}
                    className={`px-3 py-2 text-sm font-medium transition-colors ${
                      downloadFormat === 'jpg'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-background text-foreground hover:bg-muted'
                    }`}
                  >
                    JPG
                  </button>
                </div>
                <Button variant="neon" size="lg" className="flex-1 max-w-xs" onClick={handleDownload}>
                  <Download className="w-5 h-5 mr-2" />
                  {t.download}
                </Button>
              </div>
            )}
          </section>
        </div>
      </main>

      {/* Below-the-fold sections - lazy loaded */}
      <Suspense fallback={null}>
        <SocialShare />
      </Suspense>

      <Suspense fallback={null}>
        <SEOIntroSection />
      </Suspense>

      <Suspense fallback={null}>
        <FeaturesSection />
      </Suspense>

      <Suspense fallback={null}>
        <FAQSection />
      </Suspense>

      <Suspense fallback={null}>
        <Footer />
      </Suspense>

    </div>
  );
};

export default CollageEditor;